import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import permissionApi from "api/permissionApi";

// ============== REST THUNKS (unchanged) ==============
export const fetchPermissions = createAsyncThunk(
  "permission/fetch",
  async ({ roleId, page = 1, pageSize = 100 }, { rejectWithValue }) => {
    try {
      const res = await permissionApi.getByRole(roleId, page, pageSize);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || "Failed to load");
    }
  }
);

export const fetchResourceOptions = createAsyncThunk(
  "permission/options",
  async (_, { rejectWithValue }) => {
    try {
      const res = await permissionApi.getOptions();
      return res.actions.POST.resource.choices;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.detail || "Failed to load options"
      );
    }
  }
);

export const savePermissions = createAsyncThunk(
  "permission/save",
  async ({ roleId, permissions, originalPermissions }, { rejectWithValue }) => {
    try {
      const changed = permissions
        .map((curr) => {
          const orig = originalPermissions.find((o) => o.id === curr.id);
          if (!orig) return null;

          const diff = {};
          let hasDiff = false;

          [
            "can_create",
            "can_read",
            "can_update",
            "can_delete",
            "can_import",
            "can_export",
          ].forEach((field) => {
            if (curr[field] !== orig[field]) {
              diff[field] = curr[field];
              hasDiff = true;
            }
          });

          if (!hasDiff) return null;
          return { id: curr.id, ...diff };
        })
        .filter(Boolean);

      if (changed.length === 0) return true;

      const payload = changed.map((p) => ({ id: p.id, role: roleId, ...p }));
      await permissionApi.bulkUpdate(payload);
      return true;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// NEW: Bulk Create Thunk
export const bulkCreatePermissions = createAsyncThunk(
  'permission/bulkCreate',
  async ({ roleId, resources }, { rejectWithValue }) => {
    try {
      const payload = resources.map(resource => ({
        role: roleId,
        resource,
        can_create: false,
        can_read: true,
        can_update: false,
        can_delete: false,
        can_import: false,
        can_export: false,
      }));

      const response = await permissionApi.bulkCreate(payload);
      return response; // backend should return array of created permissions
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.detail ||
        e.response?.data ||
        'Failed to bulk create permissions'
      );
    }
  }
);

export const createPermission = createAsyncThunk(
  "permission/create",
  async ({ roleId, resource }, { rejectWithValue }) => {
    try {
      const payload = {
        role: roleId,
        resource,
        can_create: false,
        can_read: true,
        can_update: false,
        can_delete: false,
        can_import: false,
        can_export: false,
      };
      const res = await permissionApi.create(payload);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const deletePermission = createAsyncThunk(
  "permission/delete",
  async (id, { rejectWithValue }) => {
    try {
      await permissionApi.delete(id);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || "Delete failed");
    }
  }
);

// ============== SLICE WITH SEPARATE MY & CURRENT ROLE PERMISSIONS ==============
const permissionSlice = createSlice({
  name: "permission",
  initialState: {
    myPermissions: [], // ← YOUR OWN PERMISSIONS (RBAC)
    myPermissionsLoading: true,
    currentRolePermissions: [], // ← PERMISSIONS OF THE ROLE BEING EDITED
    resourceOptions: [],
    isLoading: false,
    isLoadingOptions: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    // Toggle individual permission in currentRolePermissions
    togglePermission: (state, action) => {
      const { permId, field } = action.payload;
      const p = state.currentRolePermissions.find((x) => x.id === permId);
      if (p) p[field] = !p[field];
    },

    // Toggle ALL permissions for ALL resources
    toggleAllPermissions: (state, action) => {
      const enabled = action.payload;
      state.currentRolePermissions = state.currentRolePermissions.map((p) => ({
        ...p,
        can_create: enabled,
        can_read: enabled,
        can_update: enabled,
        can_delete: enabled,
        can_import: enabled,
        can_export: enabled,
      }));
    },

    // Toggle ALL permissions for a SINGLE resource (per card)
    toggleAllForResource: (state, action) => {
      const { permId, enabled } = action.payload;
      state.currentRolePermissions = state.currentRolePermissions.map((p) =>
        p.id === permId
          ? {
              ...p,
              can_create: enabled,
              can_read: enabled,
              can_update: enabled,
              can_delete: enabled,
              can_import: enabled,
              can_export: enabled,
            }
          : p
      );
    },

    // FULL SYNC (your own permissions from WebSocket)
    syncPermissionsFromWS: (state, action) => {
      state.myPermissions = action.payload;
      state.myPermissionsLoading = false;
    },

    // PARTIAL UPDATE (your own permission changed)
    updatePermissionFromWS: (state, action) => {
      const updated = action.payload;
      const index = state.myPermissions.findIndex((p) => p.id === updated.id);
      if (index !== -1) {
        state.myPermissions[index] = updated;
      } else {
        state.myPermissions.push(updated);
      }
    },

    // Set permissions for the role being edited
    setCurrentRolePermissions: (state, action) => {
      state.currentRolePermissions = action.payload;
    },

    // Clear when modal closes
    clearCurrentRolePermissions: (state) => {
      state.currentRolePermissions = [];
    },
  },
  extraReducers: (builder) => {
    // fetchPermissions → only sets currentRolePermissions via dispatch in RolePage
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPermissions.fulfilled, (state) => {
        state.isLoading = false;
        // DO NOT touch myPermissions here
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchResourceOptions.pending, (state) => {
        state.isLoadingOptions = true;
      })
      .addCase(fetchResourceOptions.fulfilled, (state, action) => {
        state.isLoadingOptions = false;
        state.resourceOptions = action.payload;
      })
      .addCase(fetchResourceOptions.rejected, (state, action) => {
        state.isLoadingOptions = false;
        state.error = action.payload;
      });

    builder
      .addCase(savePermissions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(savePermissions.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(savePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
// Bulk Create
    builder
      .addCase(bulkCreatePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkCreatePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally append newly created permissions to currentRolePermissions
        if (action.payload && Array.isArray(action.payload)) {
          state.currentRolePermissions = [
            ...state.currentRolePermissions,
            ...action.payload,
          ];
        }
      })
      .addCase(bulkCreatePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
    // Optional: Auto-add created permission to currentRolePermissions
    builder.addCase(createPermission.fulfilled, (state, action) => {
      const newPerm = action.payload;
      if (
        newPerm &&
        !state.currentRolePermissions.find((p) => p.id === newPerm.id)
      ) {
        state.currentRolePermissions.push(newPerm);
      }
    });

    // Optional: Auto-remove deleted permission
    builder.addCase(deletePermission.fulfilled, (state, action) => {
      const deletedId = action.payload;
      state.currentRolePermissions = state.currentRolePermissions.filter(
        (p) => p.id !== deletedId
      );
    });
  },
});

// Export all actions
export const {
  clearError,
  togglePermission,
  toggleAllPermissions,
  toggleAllForResource,
  syncPermissionsFromWS,
  updatePermissionFromWS,
  setCurrentRolePermissions,
  clearCurrentRolePermissions,
} = permissionSlice.actions;

export default permissionSlice.reducer;
